import type { RiskWorkspace } from '@app/types'
import { useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { selectedRiskProfileWorkspaceAtom, riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
export type SelectedWorkspaceFromUrlState = {
  workspaceId: string | null
  selectedWorkspace: RiskWorkspace | null
  isLoading: boolean
  isError: boolean
  isMissingWorkspaceParam: boolean
  isWorkspaceNotFound: boolean
}

export const useSelectedWorkspaceFromUrl = (): SelectedWorkspaceFromUrlState => {
  const [searchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspace')

  const workspaces = useAtomValue(riskAvailableWorkspacesAtom)
  const selectedWorkspace = useAtomValue(selectedRiskProfileWorkspaceAtom)
  const setSelectedWorkspace = useSetAtom(selectedRiskProfileWorkspaceAtom)

  const resolvedWorkspace = useMemo(() => {
    if (workspaceId === null) return null

    return workspaces.data?.find((workspace) => workspace.id === workspaceId) ?? null
  }, [workspaceId, workspaces.data])

  useEffect(() => {
    if (workspaces.isLoading) return

    if (workspaceId === null) {
      if (selectedWorkspace !== null) {
        setSelectedWorkspace(null)
      }
      return
    }

    if (resolvedWorkspace !== null && selectedWorkspace?.id !== resolvedWorkspace.id) {
      setSelectedWorkspace(resolvedWorkspace)
    }
  }, [
    resolvedWorkspace,
    selectedWorkspace,
    setSelectedWorkspace,
    workspaceId,
    workspaces.isLoading,
  ])

  return {
    workspaceId,
    selectedWorkspace: resolvedWorkspace ?? (workspaces.isLoading ? selectedWorkspace : null),
    isLoading: workspaces.isLoading,
    isError: workspaces.isError,
    isMissingWorkspaceParam: workspaceId === null,
    isWorkspaceNotFound:
      workspaceId !== null && !workspaces.isLoading && resolvedWorkspace === null,
  }
}
